import { supabase } from './supabase';

export interface UploadedImage {
  url: string;
  path: string;
}

export const storageService = {
  // Download image from DALL-E 3 URL and upload to Supabase Storage
  async downloadAndUploadImage(
    dalleImageUrl: string, 
    storyId: string, 
    pageNumber: number
  ): Promise<UploadedImage> {
    try {
      // Download the image from DALL-E 3
      const response = await fetch(dalleImageUrl);
      if (!response.ok) {
        throw new Error(`Failed to download image: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Create a file from the blob
      const file = new File([blob], `page-${pageNumber}.png`, { type: 'image/png' });
      
      // Generate unique filename
      const timestamp = Date.now();
      const fileName = `story-${storyId}-page-${pageNumber}-${timestamp}.png`;
      const filePath = `stories/${storyId}/${fileName}`;
      
      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('story-images')
        .upload(filePath, file, {
          contentType: 'image/png',
          cacheControl: '3600'
        });
      
      if (uploadError) {
        throw new Error(`Failed to upload image: ${uploadError.message}`);
      }
      
      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('story-images')
        .getPublicUrl(filePath);
      
      return {
        url: publicUrl,
        path: filePath
      };
    } catch (error) {
      console.error('Error in downloadAndUploadImage:', error);
      throw new Error(`Failed to process image: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Delete images for a story (cleanup)
  async deleteStoryImages(storyId: string): Promise<void> {
    try {
      const { data: files, error: listError } = await supabase.storage
        .from('story-images')
        .list(`stories/${storyId}`);
      
      if (listError) {
        console.error('Error listing story images:', listError);
        return;
      }
      
      if (files && files.length > 0) {
        const filePaths = files.map(file => `stories/${storyId}/${file.name}`);
        
        const { error: deleteError } = await supabase.storage
          .from('story-images')
          .remove(filePaths);
        
        if (deleteError) {
          console.error('Error deleting story images:', deleteError);
        }
      }
    } catch (error) {
      console.error('Error in deleteStoryImages:', error);
    }
  },

  // Get storage usage info
  async getStorageUsage(): Promise<{ used: number; total: number }> {
    try {
      // This would require a custom function in Supabase
      // For now, return a placeholder
      return {
        used: 0,
        total: 1024 * 1024 * 1024 // 1GB
      };
    } catch (error) {
      console.error('Error getting storage usage:', error);
      return { used: 0, total: 0 };
    }
  }
}; 